import { useEffect } from 'react';

/**
 * Shared dismiss behaviour for the unified dropdown system: closes on an
 * outside mousedown (ignoring clicks on the dropdown or its trigger) and on
 * Escape. `onClose` should be stable or memoised by the caller.
 *
 * `enabled` (default true) gates the listeners — pass the dropdown's `open`
 * state for in-place components (SmartSelect/SearchAhead) so Escape only does
 * anything while open. On Escape we stopPropagation so an open dropdown consumes
 * the key and a host panel/modal's own Escape handler doesn't also fire; when
 * the dropdown is closed (enabled = false) the host keeps handling Escape.
 * Components that mount only while open (ActionPicker) can leave `enabled` true.
 */
export const useDropdownDismiss = (
  dropdownRef: React.RefObject<HTMLElement>,
  triggerRef: React.RefObject<HTMLElement>,
  onClose: () => void,
  enabled = true,
) => {
  useEffect(() => {
    if (!enabled) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [dropdownRef, triggerRef, onClose, enabled]);
};
